import React, { useState, useCallback, useMemo } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { BiPencil, BiSolidBusiness, BiSolidMap } from 'react-icons/bi';
import { toast } from 'react-toastify';
import type { Drivers, Locations } from '@gql/base-types';
import type { GetUserDetailsQuery } from '../queries/getUserDetails.query.generated';
import { useInsertDriverHomeLocationMutation } from '../../drivers/queries/insertDriverHomeLocation.mutation.generated';
import { useUpdateDriverHomeLocationMutation } from '../../drivers/queries/updateDriverHomeLocation.mutation.generated';
import { useRooftop } from '@store';
import DriverHomeAddressField from '@features/drivers/components/DriverHomeAddressField';
import DriverSchedule from '@features/drivers/components/DriverSchedule';
import { getRooftopTimezone } from '@utils/timezoneUtils';

type UserDetail = NonNullable<GetUserDetailsQuery['users']>[0];

interface UserAvailabilityTabProps {
  driver: Drivers;
  /** The full user object — needed for usertocustomers (rooftop list) */
  user?: UserDetail;
  refetch?: () => void;
}

const formatAddress = (location: Locations | null | undefined): string => {
  if (!location) return '';
  const parts = [location.address_line_one, location.city, location.state, location.zip_code].filter(Boolean);
  return parts.join(', ') || '';
};

const UserAvailabilityTab: React.FC<UserAvailabilityTabProps> = ({ driver, user, refetch }) => {
  const rooftop = useRooftop();
  const [insertLocation] = useInsertDriverHomeLocationMutation();
  const [updateDriverLocation] = useUpdateDriverHomeLocationMutation();
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  const currentLocation = driver.home_location as Locations | null;
  const hasLocation = !!currentLocation;

  const timezone = currentLocation?.timezone
    || driver.region?.timezone
    || getRooftopTimezone(rooftop);

  // Build list of rooftops with locations for the "From rooftop" shortcut
  const rooftopOptions = useMemo(() => {
    if (!user?.users_usertocustomers) return [];
    return user.users_usertocustomers
      .filter((utc: any) => utc.customer?.location)
      .map((utc: any) => ({
        id: utc.customer.id,
        name: utc.customer.name,
        location: utc.customer.location as Locations,
      }));
  }, [user?.users_usertocustomers]);

  const handleLocationChange = useCallback(
    async (location: Locations | null) => {
      if (!location) return;

      setSaving(true);
      try {
        let locationId: number | undefined | null = location.id;

        if (!locationId) {
          const result = await insertLocation({
            variables: {
              location: {
                customer_id: location.customer_id,
                address_line_one: location.address_line_one,
                address_line_two: location.address_line_two,
                city: location.city,
                state: location.state,
                zip_code: location.zip_code,
                latitude: location.latitude,
                longitude: location.longitude,
                place_id: location.place_id,
                address: (location as any).address,
                name: (location as any).name,
                timezone: location.timezone,
                type: 'driver-home',
              },
            },
          });
          locationId = result.data?.insert_locations_one?.id ?? null;

          if (!locationId) {
            throw new Error('Failed to create location');
          }
        }

        await updateDriverLocation({
          variables: {
            driverId: driver.id,
            homeLocationId: locationId,
          },
        });

        toast.success('Primary location updated');
        setEditing(false);
        refetch?.();
      } catch (err: any) {
        console.error('Failed to save primary location:', err);
        toast.error('Failed to save primary location');
      } finally {
        setSaving(false);
      }
    },
    [driver.id, insertLocation, updateDriverLocation, refetch],
  );

  const handleRooftopSelect = useCallback(
    (rooftopOption: { location: Locations } | null) => {
      if (rooftopOption) handleLocationChange(rooftopOption.location);
    },
    [handleLocationChange],
  );

  // Determine if we show the edit state: always if no location, or if user clicked edit
  const showEditState = !hasLocation || editing;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Primary Location — compact bar */}
      <Paper sx={{ px: 3, py: showEditState ? 2.5 : 1.5, borderRadius: '12px' }}>
        {showEditState ? (
          /* ── Edit / unset state ── */
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Primary location — where this driver typically starts their day
            </Typography>

            {!hasLocation && (
              <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
                Not set — using {rooftop?.location?.timezone ? 'rooftop' : 'default'} timezone ({timezone})
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <DriverHomeAddressField
                  value={currentLocation}
                  onChange={handleLocationChange}
                  placeholder="Search for a location..."
                  driverId={driver.id}
                  customerId={driver.user?.customer_id || rooftop?.id || null}
                  disabled={saving}
                />
              </Box>

              {rooftopOptions.length > 0 && (
                <Autocomplete
                  options={rooftopOptions}
                  getOptionLabel={opt => opt.name}
                  onChange={(_, value) => handleRooftopSelect(value)}
                  disabled={saving}
                  size="small"
                  blurOnSelect
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder="From rooftop..."
                      variant="outlined"
                      size="small"
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <BiSolidBusiness size={14} style={{ marginRight: 4, opacity: 0.5, flexShrink: 0 }} />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                  sx={{ minWidth: 200, maxWidth: 280 }}
                />
              )}

              {hasLocation && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setEditing(false)}
                  sx={{ height: '40px', textTransform: 'none' }}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </>
        ) : (
          /* ── Collapsed read state — single line ── */
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BiSolidMap size={16} style={{ opacity: 0.5, flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
              Primary location:
            </Typography>
            <Typography variant="body2" noWrap sx={{ flex: 1 }}>
              {formatAddress(currentLocation)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              ({currentLocation?.timezone || timezone})
            </Typography>
            <Tooltip title="Change primary location">
              <IconButton size="small" onClick={() => setEditing(true)}>
                <BiPencil size={14} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Paper>

      {/* Weekly Schedule + Overrides */}
      <DriverSchedule driver={driver} />
    </Box>
  );
};

export default UserAvailabilityTab;
